/**
 * Deterministic random number generation.
 *
 * `Math.random()` is forbidden anywhere in gameplay. Every stochastic decision —
 * recoil scatter, AI reaction jitter, ammo brick drops, particle spread — draws
 * from a seeded stream so a given seed replays identically. That is what makes
 * screenshot regression testing and bug reproduction possible at all.
 *
 * xorshift128 is used rather than a hash-based generator because it is fast,
 * has a long period (2^128 - 1), and carries its state in four integers we can
 * snapshot and restore cheaply.
 */
export class Rng {
  constructor(seed = 0x9e3779b9) {
    this.reseed(seed);
  }

  reseed(seed) {
    // SplitMix32 the scalar seed into four well-mixed words. Seeding xorshift
    // directly from a small integer leaves the first few outputs correlated.
    let z = seed >>> 0;
    const next = () => {
      z = (z + 0x9e3779b9) >>> 0;
      let t = z;
      t = Math.imul(t ^ (t >>> 16), 0x21f0aaad) >>> 0;
      t = Math.imul(t ^ (t >>> 15), 0x735a2d97) >>> 0;
      return (t ^ (t >>> 15)) >>> 0;
    };
    this._x = next() || 1;
    this._y = next() || 2;
    this._z = next() || 3;
    this._w = next() || 4;
    this._seed = seed >>> 0;
  }

  /** Raw 32-bit unsigned draw. */
  u32() {
    let t = this._x ^ (this._x << 11);
    t >>>= 0;
    t = (t ^ (t >>> 8)) >>> 0;
    this._x = this._y;
    this._y = this._z;
    this._z = this._w;
    this._w = (this._w ^ (this._w >>> 19) ^ t) >>> 0;
    return this._w;
  }

  /** Uniform in [0, 1). */
  float() {
    // 2^-32. Dividing by 0x100000000 keeps the full 32 bits of entropy.
    return this.u32() * 2.3283064365386963e-10;
  }

  /** Uniform in [min, max). */
  range(min, max) {
    return min + (max - min) * this.float();
  }

  /** Uniform integer in [min, max] inclusive. */
  int(min, max) {
    return min + Math.floor(this.float() * (max - min + 1));
  }

  /** Uniform in [-m, m). */
  signed(m = 1) {
    return (this.float() * 2 - 1) * m;
  }

  /** True with probability p. */
  chance(p) {
    return this.float() < p;
  }

  /**
   * Standard normal via Box-Muller. Used for recoil scatter and AI timing so
   * variation clusters around the mean rather than spreading flat — flat noise
   * reads as erratic, gaussian noise reads as organic.
   */
  gaussian() {
    if (this._spare !== undefined) {
      const s = this._spare;
      this._spare = undefined;
      return s;
    }
    let u, v, s;
    do {
      u = this.float() * 2 - 1;
      v = this.float() * 2 - 1;
      s = u * u + v * v;
    } while (s >= 1 || s === 0);
    const f = Math.sqrt((-2 * Math.log(s)) / s);
    this._spare = v * f;
    return u * f;
  }

  /** Random element of an array. */
  pick(arr) {
    return arr[Math.floor(this.float() * arr.length)];
  }

  /** In-place Fisher-Yates. */
  shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this.float() * (i + 1));
      const t = arr[i];
      arr[i] = arr[j];
      arr[j] = t;
    }
    return arr;
  }

  /**
   * Fork an independent stream. Subsystems take their own fork at init so that,
   * for example, adding a particle draw does not shift the AI's random sequence
   * and invalidate every downstream test.
   */
  fork(tag = 0) {
    return new Rng((this.u32() ^ Math.imul(tag | 0, 0x85ebca6b)) >>> 0);
  }

  save() {
    return { x: this._x, y: this._y, z: this._z, w: this._w };
  }

  restore(s) {
    this._x = s.x; this._y = s.y; this._z = s.z; this._w = s.w;
    this._spare = undefined;
  }
}
